import { useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SURVEY_QUESTIONS, type Question } from "../lib/survey-config";
import { isValidParticipant, sanitizeParticipant, storageKey } from "../lib/participant";
import { storageGet, storageRemove, storageSet } from "../lib/storage";
import {
  buildSubmission,
  fromPersisted,
  isPersisted,
  makeInitialState,
  surveyReducer,
  toPersisted,
  type SurveyState,
} from "../lib/survey-state";
import { api } from "../lib/api";
import { QuestionFrame } from "../components/QuestionFrame";
import { TextQuestion } from "../components/questions/TextQuestion";
import { LongTextQuestion } from "../components/questions/LongTextQuestion";
import { SingleSelectQuestion } from "../components/questions/SingleSelectQuestion";
import { MultiSelectQuestion } from "../components/questions/MultiSelectQuestion";
import { SliderQuestion } from "../components/questions/SliderQuestion";
import type { MultiSelectValue } from "../components/questions/types";

type Boot =
  | { phase: "loading" }
  | { phase: "resume"; saved: SurveyState; savedAt: string }
  | { phase: "ready"; seed: SurveyState };

export function Survey() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const participant = sanitizeParticipant(params.get("p") ?? "");

  const [boot, setBoot] = useState<Boot>({ phase: "loading" });

  useEffect(() => {
    if (!isValidParticipant(participant)) {
      navigate("/", { replace: true });
      return;
    }
    const stored = storageGet<unknown>(storageKey(participant));
    if (isPersisted(stored) && stored.participant === participant) {
      setBoot({ phase: "resume", saved: fromPersisted(stored), savedAt: stored.savedAt });
    } else {
      setBoot({ phase: "ready", seed: makeInitialState(participant) });
    }
  }, [participant, navigate]);

  if (boot.phase === "loading") return null;

  if (boot.phase === "resume") {
    return (
      <ResumeDialog
        savedAt={boot.savedAt}
        index={boot.saved.index}
        total={SURVEY_QUESTIONS.length}
        onResume={() => setBoot({ phase: "ready", seed: boot.saved })}
        onRestart={() => {
          storageRemove(storageKey(participant));
          setBoot({ phase: "ready", seed: makeInitialState(participant) });
        }}
      />
    );
  }

  return <SurveyRunner key={boot.seed.submissionId} seed={boot.seed} />;
}

function ResumeDialog({
  savedAt,
  index,
  total,
  onResume,
  onRestart,
}: {
  savedAt: string;
  index: number;
  total: number;
  onResume: () => void;
  onRestart: () => void;
}) {
  const minutes = Math.max(1, Math.round((Date.now() - new Date(savedAt).getTime()) / 60000));
  return (
    <div className="flex flex-1 flex-col justify-center gap-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Da war doch was…</h1>
        <p className="text-fg-muted leading-relaxed">
          Du hast vor ungefähr <strong>{minutes} {minutes === 1 ? "Minute" : "Minuten"}</strong>{" "}
          mit dem Survey angefangen — bei Frage <strong>{index + 1} von {total}</strong>.
        </p>
        <p className="text-fg-muted leading-relaxed">
          Möchtest du da weitermachen oder lieber neu starten?
        </p>
      </header>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onResume}
          className="rounded-input bg-accent px-5 py-2.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
        >
          Weitermachen
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="rounded-input border border-border px-5 py-2.5 text-sm transition hover:border-fg-muted"
        >
          Neu starten
        </button>
      </div>
    </div>
  );
}

function SurveyRunner({ seed }: { seed: SurveyState }) {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(surveyReducer, seed);
  const [valid, setValid] = useState(false);
  const key = storageKey(state.participant);

  useEffect(() => {
    if (state.status === "submitted") return;
    storageSet(key, toPersisted(state));
  }, [state, key]);

  const total = SURVEY_QUESTIONS.length;
  const isLast = state.index === total - 1;
  const question = SURVEY_QUESTIONS[state.index] as Question | undefined;

  const node = useMemo(() => {
    if (!question) return null;
    const value = state.answers[question.id];
    const update = (next: string | number | MultiSelectValue) =>
      dispatch({ type: "answer", id: question.id, value: next });
    return renderQuestion(question, value, update, setValid);
  }, [question, state.answers]);

  async function submit() {
    if (state.status === "submitting") return;
    dispatch({ type: "submit_start" });
    try {
      const submission = buildSubmission(state);
      await api.submitResponse(submission);
      dispatch({ type: "submit_success" });
      storageRemove(key);
      navigate("/done");
    } catch (err) {
      const msg =
        err && typeof err === "object" && "status" in err
          ? `Server hat mit Fehler ${(err as { status: number }).status} geantwortet`
          : "Verbindung zum Server fehlgeschlagen";
      dispatch({ type: "submit_error", message: msg });
    }
  }

  if (!question) return null;

  if (state.status === "submitting" || state.status === "error") {
    return (
      <SubmitState
        state={state}
        onRetry={submit}
        onBack={() => dispatch({ type: "back" })}
      />
    );
  }

  return (
    <QuestionFrame
      index={state.index}
      total={total}
      direction={state.direction}
      canBack={state.index > 0}
      canNext={valid}
      isLast={isLast}
      onBack={() => dispatch({ type: "back" })}
      onNext={() => {
        if (isLast) {
          submit();
        } else {
          dispatch({ type: "next" });
        }
      }}
    >
      {node}
    </QuestionFrame>
  );
}

function SubmitState({
  state,
  onRetry,
  onBack,
}: {
  state: SurveyState;
  onRetry: () => void;
  onBack: () => void;
}) {
  if (state.status === "submitting") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-fg-muted">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-border border-t-accent" />
        <p className="text-sm">Wird abgeschickt …</p>
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col justify-center gap-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">Hat nicht geklappt</h1>
        <p className="text-fg-muted leading-relaxed">
          {state.errorMessage}.{" "}
          Deine Antworten sind aber sicher gespeichert — probier es einfach nochmal.
        </p>
      </header>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="rounded-input bg-accent px-5 py-2.5 text-sm font-medium text-accent-fg transition hover:opacity-90"
        >
          Nochmal versuchen
        </button>
        <button
          type="button"
          onClick={onBack}
          className="rounded-input border border-border px-5 py-2.5 text-sm transition hover:border-fg-muted"
        >
          Zurück zur letzten Frage
        </button>
      </div>
    </div>
  );
}

function renderQuestion(
  q: Question,
  value: string | number | MultiSelectValue | undefined,
  onChange: (next: string | number | MultiSelectValue) => void,
  onValid: (v: boolean) => void,
) {
  switch (q.type) {
    case "shortText":
      return (
        <TextQuestion
          question={q}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          onValid={onValid}
          autoFocus
        />
      );
    case "longText":
      return (
        <LongTextQuestion
          question={q}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          onValid={onValid}
          autoFocus
        />
      );
    case "singleSelect":
      return (
        <SingleSelectQuestion
          question={q}
          value={typeof value === "string" ? value : ""}
          onChange={onChange}
          onValid={onValid}
          autoFocus
        />
      );
    case "multiSelect": {
      const ms: MultiSelectValue =
        value && typeof value === "object" && "selected" in value
          ? value
          : { selected: [], other: "" };
      return (
        <MultiSelectQuestion
          question={q}
          value={ms}
          onChange={onChange}
          onValid={onValid}
          autoFocus
        />
      );
    }
    case "slider": {
      const num = typeof value === "number" ? value : (q.defaultValue ?? q.min);
      return (
        <SliderQuestion
          question={q}
          value={num}
          onChange={onChange}
          onValid={onValid}
          autoFocus
        />
      );
    }
  }
}
